'use client'

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Button } from '@repo/ui/button'
import { Nav } from '@/components/layout/nav'
import { Footer } from '@/components/layout/footer'
import { PremiumCard, PremiumHeading, PremiumSection } from '@/components/shared'
import {
  getAllResources,
  getResourceBySlug,
  type Resource,
} from '@/content/resources'
import { getAllGuides, getGuideBySlug, getGuideResource, type Guide } from '@/content/guides'
import { cn } from '@repo/ui/lib/utils'
import { YOUTUBE_URL } from '@/shared/lib/constants'
import { OAC } from '@/content/oac'
import {
  BAD_FIT,
  BOOKING_HREF,
  FAQ,
  FOUNDING,
  GOOD_FIT,
  INCLUDED,
  NOT_INCLUDED,
  REFUND_POLICY,
  RESCHEDULE_POLICY,
  REVIEW,
} from '@/content/offer'

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    /* Nav is sticky (not fixed), so main needs no top offset — the old pt-16
       left a dead band and a stray rule under the header. */
    <div className="flex min-h-screen flex-col bg-[#0e1110]">
      <Nav />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  )
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex rounded-full border border-[#80cca5]/20 bg-[#80cca5]/10 px-3 py-1 text-sm font-medium text-[#80cca5]">
      {children}
    </span>
  )
}

function CopyTemplateButton({ template }: { template: string }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(template)
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  const label =
    copyState === 'copied'
      ? 'Copied template'
      : copyState === 'failed'
        ? 'Copy failed'
        : 'Copy Template'

  return (
    <Button
      variant="secondary"
      size="cta"
      onClick={copyTemplate}
      className="print:hidden border border-white/10 bg-white/[0.02] text-white hover:border-[#80cca5]/40 hover:bg-white/[0.04]"
    >
      {label}
    </Button>
  )
}

function EmptyState({ label }: { label: string }) {
  return (
    <PageFrame>
      <PremiumSection className="min-h-[70vh]" containerClassName="max-w-3xl">
        <PremiumCard hover={false} className="text-center">
          <PremiumHeading as="h1">{label}</PremiumHeading>
          <p className="mb-8 text-lg text-slate-300">
            This page is not available yet. Browse the current guides and resources.
          </p>
          <Link
            to="/resources"
            className="inline-flex rounded-lg bg-[#80cca5] px-6 py-3 font-semibold text-[#0e1110] transition-colors hover:bg-[#6bb890]"
          >
            See Resources
          </Link>
        </PremiumCard>
      </PremiumSection>
    </PageFrame>
  )
}

function ResourceCard({ resource }: { resource: Resource }) {
  return (
    <PremiumCard className="flex h-full flex-col">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Pill>{resource.category}</Pill>
        <Pill>{resource.timeToUse}</Pill>
        <Pill>{resource.download.format} download</Pill>
        {resource.featured && <Pill>Featured</Pill>}
      </div>
      <h2 className="mb-3 text-2xl font-bold text-white">{resource.title}</h2>
      <p className="mb-6 flex-1 text-slate-300 leading-relaxed">{resource.summary}</p>
      <p className="mb-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm text-slate-300 leading-relaxed">
        <span className="font-semibold text-[#80cca5]">Outcome:</span> {resource.outcome}
      </p>
      <Link
        to="/resources/$slug"
        params={{ slug: resource.slug }}
        className="font-semibold text-[#80cca5] transition-colors hover:text-[#6bb890]"
      >
        Open resource →
      </Link>
    </PremiumCard>
  )
}

function GuideCard({ guide }: { guide: Guide }) {
  return (
    <PremiumCard className="flex h-full flex-col">
      <div className="mb-5 flex flex-wrap gap-3">
        {guide.tools.slice(0, 3).map((tool) => (
          <Pill key={tool}>{tool}</Pill>
        ))}
      </div>
      <h2 className="mb-3 text-2xl font-bold text-white">{guide.title}</h2>
      <p className="mb-6 flex-1 text-slate-300 leading-relaxed">{guide.summary}</p>
      <p className="mb-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm text-slate-300 leading-relaxed">
        <span className="font-semibold text-[#80cca5]">Outcome:</span> {guide.outcome}
      </p>
      <Link
        to="/guides/$slug"
        params={{ slug: guide.slug }}
        className="font-semibold text-[#80cca5] transition-colors hover:text-[#6bb890]"
      >
        Read guide →
      </Link>
    </PremiumCard>
  )
}

export function ResourcesIndexPage() {
  const resources = getAllResources()

  return (
    <PageFrame>
      <PremiumSection containerClassName="max-w-5xl">
        <div className="mb-14 max-w-3xl">
          <Pill>Free resources</Pill>
          <PremiumHeading as="h1" className="mt-6">
            Practical AI Developer
          </PremiumHeading>
          <p className="text-xl text-slate-300 leading-relaxed">
            Printable kits, templates, and checklists for the Plan → Prompt → Build → Review → Ship workflow.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          {resources.map((resource) => (
            <ResourceCard key={resource.slug} resource={resource} />
          ))}
        </div>
        <OfferFooterCta />
      </PremiumSection>
    </PageFrame>
  )
}

export function ResourceDetailPage({ slug }: { slug: string }) {
  const resource = getResourceBySlug(slug)

  if (!resource) return <EmptyState label="Resource not found" />

  const relatedGuide = resource.relatedGuideSlug
    ? getGuideBySlug(resource.relatedGuideSlug)
    : undefined

  return (
    <PageFrame>
      <PremiumSection containerClassName="max-w-4xl">
        <article className="print-resource">
          <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <Pill>{resource.eyebrow}</Pill>
              <PremiumHeading as="h1" className="mt-6">
                {resource.title}
              </PremiumHeading>
              <p className="max-w-3xl text-xl text-slate-300 leading-relaxed">{resource.summary}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Pill>{resource.category}</Pill>
                <Pill>{resource.timeToUse}</Pill>
                <Pill>{resource.format}</Pill>
              </div>
            </div>
            <div className="print:hidden flex shrink-0 flex-col gap-3 sm:flex-row md:flex-col">
              <a
                href={resource.download.href}
                download
                className="inline-flex justify-center rounded-lg bg-[#80cca5] px-8 py-4 font-semibold text-[#0e1110] transition-colors hover:bg-[#6bb890]"
              >
                {resource.download.label}
              </a>
              <Button
                variant="primary-cta"
                size="cta"
                onClick={() => window.print()}
              >
                Print / Save as PDF
              </Button>
              {resource.copyTemplate && <CopyTemplateButton template={resource.copyTemplate} />}
            </div>
          </div>

          <PremiumCard hover={false} className="mb-8 print:hidden">
            <h2 className="mb-3 text-2xl font-bold text-white">Download options</h2>
            <p className="text-slate-300 leading-relaxed">
              The downloadable file is Markdown: readable in any editor, easy to modify, printable, and simple to convert to PDF later. You can also use your browser’s print dialog on this page to print or save the resource as a PDF now.
            </p>
          </PremiumCard>

          <PremiumCard hover={false} className="mb-8 print-card">
            <h2 className="mb-3 text-2xl font-bold text-white">Outcome</h2>
            <p className="text-slate-300 leading-relaxed">{resource.outcome}</p>
          </PremiumCard>

          <div className="mb-8 grid gap-6 md:grid-cols-2">
            <PremiumCard hover={false} className="print-card">
              <h2 className="mb-3 text-2xl font-bold text-white">Who this is for</h2>
              <p className="text-slate-300 leading-relaxed">{resource.audience}</p>
            </PremiumCard>

            <PremiumCard hover={false} className="print-card">
              <h2 className="mb-3 text-2xl font-bold text-white">Use when</h2>
              <ul className="space-y-3">
                {resource.useWhen.map((item) => (
                  <li key={item} className="flex gap-3 text-slate-200">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#80cca5]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </PremiumCard>
          </div>

          <PremiumCard hover={false} className="mb-8 print-card">
            <h2 className="mb-3 text-2xl font-bold text-white">What’s inside</h2>
            <ul className="grid gap-3 md:grid-cols-2">
              {resource.whatsInside.map((item) => (
                <li key={item} className="flex gap-3 text-slate-200">
                  <span className="text-[#80cca5]">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </PremiumCard>

          <PremiumCard hover={false} className="mb-8 print-card">
            <h2 className="mb-3 text-2xl font-bold text-white">How to use this</h2>
            <ol className="space-y-3">
              {resource.howToUse.map((item, index) => (
                <li key={item} className="flex gap-3 text-slate-200">
                  <span className="font-semibold text-[#80cca5]">{index + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>
          </PremiumCard>

          <div className="space-y-6">
            {resource.sections.map((section) => (
              <PremiumCard key={section.title} hover={false} className="print-card">
                <h2 className="mb-2 text-2xl font-bold text-white">{section.title}</h2>
                <p className="mb-5 text-slate-300 leading-relaxed">{section.description}</p>
                <ul className="space-y-3">
                  {section.checklist.map((item) => (
                    <li key={item} className="flex gap-3 text-slate-200">
                      <span className="mt-1 text-[#80cca5]">□</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </PremiumCard>
            ))}
          </div>

          {relatedGuide && (
            <PremiumCard hover={false} className="mt-8 print:hidden">
              <h2 className="mb-3 text-2xl font-bold text-white">Related guide</h2>
              <p className="mb-6 text-slate-300 leading-relaxed">{relatedGuide.summary}</p>
              <Link
                to="/guides/$slug"
                params={{ slug: relatedGuide.slug }}
                className="inline-flex rounded-lg bg-[#80cca5] px-6 py-3 font-semibold text-[#0e1110] transition-colors hover:bg-[#6bb890]"
              >
                Read {relatedGuide.title}
              </Link>
            </PremiumCard>
          )}

          <OfferFooterCta />
        </article>
      </PremiumSection>
    </PageFrame>
  )
}

export function GuidesIndexPage() {
  const guides = getAllGuides()

  return (
    <PageFrame>
      <PremiumSection containerClassName="max-w-5xl">
        <div className="mb-14 max-w-3xl">
          <Pill>Companion guides</Pill>
          <PremiumHeading as="h1" className="mt-6">
            Practical AI Workflow
          </PremiumHeading>
          <p className="text-xl text-slate-300 leading-relaxed">
            Written guides for OpenCode, Claude Code, Cursor, agents, and production-minded AI development workflows.
          </p>
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          {guides.map((guide) => (
            <GuideCard key={guide.slug} guide={guide} />
          ))}
        </div>
        <OfferFooterCta />
      </PremiumSection>
    </PageFrame>
  )
}

export function GuideDetailPage({ slug }: { slug: string }) {
  const guide = getGuideBySlug(slug)

  if (!guide) return <EmptyState label="Guide not found" />

  const resource = getGuideResource(guide)

  return (
    <PageFrame>
      <PremiumSection containerClassName="max-w-4xl">
        <article>
          <Pill>{guide.eyebrow}</Pill>
          <PremiumHeading as="h1" className="mt-6">
            {guide.title}
          </PremiumHeading>
          <p className="mb-8 text-xl text-slate-300 leading-relaxed">{guide.outcome}</p>
          <div className="mb-10 flex flex-wrap gap-3">
            {guide.tools.map((tool) => (
              <Pill key={tool}>{tool}</Pill>
            ))}
          </div>

          <div className="mb-8 grid gap-6 md:grid-cols-3">
            <PremiumCard hover={false}>
              <h2 className="mb-4 text-xl font-bold text-white">Who this is for</h2>
              <ul className="space-y-3">
                {guide.audience.map((item) => (
                  <li key={item} className="flex gap-3 text-sm text-slate-200">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#80cca5]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </PremiumCard>

            <PremiumCard hover={false}>
              <h2 className="mb-4 text-xl font-bold text-white">Practical steps</h2>
              <ol className="space-y-3">
                {guide.steps.map((item, index) => (
                  <li key={item} className="flex gap-3 text-sm text-slate-200">
                    <span className="font-semibold text-[#80cca5]">{index + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ol>
            </PremiumCard>

            <PremiumCard hover={false}>
              <h2 className="mb-4 text-xl font-bold text-white">Common mistakes</h2>
              <ul className="space-y-3">
                {guide.mistakes.map((item) => (
                  <li key={item} className="flex gap-3 text-sm text-slate-200">
                    <span className="text-[#80cca5]">×</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </PremiumCard>
          </div>

          <div className="space-y-6">
            {guide.sections.map((section) => (
              <PremiumCard key={section.title} hover={false}>
                <h2 className="mb-3 text-2xl font-bold text-white">{section.title}</h2>
                <p className="mb-5 text-slate-300 leading-relaxed">{section.body}</p>
                <ul className="space-y-3">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3 text-slate-200">
                      <span className="mt-2 size-1.5 rounded-full bg-[#80cca5]" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </PremiumCard>
            ))}
          </div>

          {resource && (
            <PremiumCard className="mt-8" hover={false}>
              <h2 className="mb-3 text-2xl font-bold text-white">Use the related resource</h2>
              <p className="mb-6 text-slate-300 leading-relaxed">{resource.summary}</p>
              <Link
                to="/resources/$slug"
                params={{ slug: resource.slug }}
                className="inline-flex rounded-lg bg-[#80cca5] px-6 py-3 font-semibold text-[#0e1110] transition-colors hover:bg-[#6bb890]"
              >
                {resource.ctaLabel}
              </Link>
            </PremiumCard>
          )}

          <OfferFooterCta />
        </article>
      </PremiumSection>
    </PageFrame>
  )
}


function BookReviewButton({
  children = 'Book a Workflow Review',
  className,
}: {
  children?: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={BOOKING_HREF}
      className={cn(
        'inline-flex justify-center rounded-lg bg-[#80cca5] px-7 py-3.5 font-semibold text-[#0e1110] transition-colors hover:bg-[#6bb890]',
        className,
      )}
    >
      {children}
    </a>
  )
}

export function OfferFooterCta() {
  return (
    <div className="mt-12 rounded-xl border border-[#232a26] bg-[#141817] p-6 md:p-8 print:hidden">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="mb-2 text-xl font-semibold text-[#f0f3f1]">
            Want help applying this to your own work?
          </h2>
          <p className="max-w-xl leading-relaxed text-[#c3ccc7]">
            I do 1:1 sessions on a screen-share — {REVIEW.duration} on whatever
            is actually slowing you down, fixing it live rather than describing
            the fix. You leave with a written plan. {REVIEW.price}.
          </p>
        </div>
        <Link
          to="/call"
          className="inline-flex shrink-0 justify-center rounded-lg border border-[#2e3833] px-6 py-3 font-semibold text-[#f0f3f1] transition-colors hover:border-[#80cca5]/50 hover:text-[#80cca5]"
        >
          See how it works →
        </Link>
      </div>
    </div>
  )
}

function CheckList({ items, marker }: { items: readonly string[]; marker: string }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex gap-3 leading-relaxed text-[#c3ccc7]">
          <span
            aria-hidden="true"
            className="mt-0.5 shrink-0 font-mono text-sm text-[#8b958f]"
          >
            {marker}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export function CoachingPage() {
  return (
    <PageFrame>
      {/* Hero */}
      <section className="bg-[#0e1110] px-4 py-20 md:py-28">
        <div className="mx-auto max-w-4xl">
          <p className="mb-5 font-mono text-sm text-[#8b958f]">
            1:1 · {REVIEW.duration} · {REVIEW.capLine}
          </p>
          <h1 className="mb-6 max-w-3xl text-4xl font-semibold tracking-tight text-[#f0f3f1] md:text-5xl">
            Ninety minutes, one to one, on how you actually work with AI.
          </h1>
          <p className="mb-8 max-w-2xl text-lg leading-relaxed text-[#c3ccc7] md:text-xl">
            Bring a codebase, a project you are planning, or an agent setup
            that keeps going wrong. We work on it together on a screen-share —
            I change things while you watch, rather than describing what you
            should change. You leave with a written plan.
          </p>

          <div className="mb-8 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-3xl font-semibold text-[#f0f3f1]">
              {REVIEW.price}
            </span>
            <span className="text-[#8b958f]">
              one session, {REVIEW.duration}, nothing recurring
            </span>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <BookReviewButton />
            <a
              href={OAC.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex justify-center rounded-lg border border-[#2e3833] px-7 py-3.5 font-semibold text-[#f0f3f1] transition-colors hover:border-[#80cca5]/50 hover:text-[#80cca5]"
            >
              Not ready? Start with {OAC.short} — free
            </a>
          </div>

          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-[#8b958f]">
            {REFUND_POLICY} {RESCHEDULE_POLICY}
          </p>
        </div>
      </section>

      {/* How it works */}
      <PremiumSection containerClassName="max-w-4xl">
        <PremiumHeading as="h2">How it works</PremiumHeading>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            {
              step: '01',
              title: 'Book and tell me what to look at',
              body: 'You get a form when you book. One sentence on what you want to work on, one on what you have already tried. A repo link if you have one — useful, not required. That is the whole prep.',
            },
            {
              step: '02',
              title: 'Ninety minutes on the real thing',
              body: 'Screen-share, and we work on your situation rather than an example one. If there is a repo we get into it; if there is not, we work on the setup, the plan, or the decision in front of you.',
            },
            {
              step: '03',
              title: 'Leave with the plan written',
              body: 'The action plan gets typed during the call in a doc you keep. Nothing is promised for later, because things promised for later do not arrive.',
            },
          ].map((item) => (
            <div
              key={item.step}
              className="rounded-xl border border-[#232a26] bg-[#141817] p-6"
            >
              <p className="mb-3 font-mono text-sm text-[#80cca5]">{item.step}</p>
              <h3 className="mb-2 text-lg font-semibold text-[#f0f3f1]">
                {item.title}
              </h3>
              <p className="leading-relaxed text-[#c3ccc7]">{item.body}</p>
            </div>
          ))}
        </div>
      </PremiumSection>

      {/* What you get / what you don't */}
      <PremiumSection containerClassName="max-w-4xl">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <PremiumHeading as="h2">What you get</PremiumHeading>
            <CheckList items={INCLUDED} marker="+" />
          </div>
          <div>
            <PremiumHeading as="h2">What this is not</PremiumHeading>
            <CheckList items={NOT_INCLUDED} marker="−" />
          </div>
        </div>
        {FOUNDING.active && (
          <p className="mt-10 border-l-2 border-[#80cca5] pl-5 leading-relaxed text-[#c3ccc7]">
            {FOUNDING.note}
          </p>
        )}
      </PremiumSection>

      {/* Fit */}
      <PremiumSection containerClassName="max-w-4xl">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <PremiumHeading as="h2">Book this if</PremiumHeading>
            <CheckList items={GOOD_FIT} marker="+" />
          </div>
          <div>
            <PremiumHeading as="h2">Do not book this if</PremiumHeading>
            <CheckList items={BAD_FIT} marker="−" />
          </div>
        </div>
      </PremiumSection>

      {/* Who I am */}
      <PremiumSection containerClassName="max-w-4xl">
        <PremiumHeading as="h2">Who you are booking</PremiumHeading>
        <div className="max-w-2xl space-y-4 leading-relaxed text-[#c3ccc7]">
          <p>
            I am Darren. I make{' '}
            <a
              href={YOUTUBE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#80cca5] underline underline-offset-4 hover:text-[#6bb890]"
            >
              DarrenBuildsAI
            </a>
            , where I work through AI coding tools on real projects rather than
            demos — and show the parts that do not work as well as the parts
            that do.
          </p>
          <p>
            I build and ship with these tools every working day. That is the
            only reason the advice is worth anything, and it is why the sessions
            stay capped at {REVIEW.capLine.toLowerCase()} — the moment this
            becomes the job instead of the thing beside the job, the advice goes
            stale.
          </p>
          <p>
            I also build{' '}
            <a
              href={OAC.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#80cca5] underline underline-offset-4 hover:text-[#6bb890]"
            >
              {OAC.name}
            </a>
            , an open-source agent framework with {OAC.stars} stars on GitHub.
            It exists because I kept hitting the same problem you probably
            have: agents that run off and produce a diff nobody can review.
            Most of what we do in a session is the same thinking, applied to
            your situation instead of a general one.
          </p>
          {/*
            TODO(darren): optional — add years and stack if you want it, but
            OAC now carries the credibility this paragraph was missing.
          */}
          <p>
            If you want to judge before spending anything, watch a couple of
            videos first. The session is the same person, working on your
            problem instead of mine.
          </p>
        </div>
      </PremiumSection>

      {/* FAQ */}
      <PremiumSection containerClassName="max-w-4xl">
        <PremiumHeading as="h2">Questions people actually ask</PremiumHeading>
        <div className="max-w-3xl divide-y divide-[#232a26] border-y border-[#232a26]">
          {FAQ.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-medium text-[#f0f3f1] marker:content-none">
                <span>{item.q}</span>
                <span
                  aria-hidden="true"
                  className="mt-1 shrink-0 font-mono text-[#8b958f] transition-transform group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <p className="mt-3 max-w-2xl leading-relaxed text-[#c3ccc7]">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </PremiumSection>

      {/* Close */}
      <PremiumSection containerClassName="max-w-4xl">
        <PremiumHeading as="h2">{REVIEW.capLine}. That is the cap.</PremiumHeading>
        <p className="mb-8 max-w-2xl leading-relaxed text-[#c3ccc7]">
          If the calendar is full, it is genuinely full — there is no timer on
          this page counting down to a fake deadline. {REFUND_POLICY}
        </p>
        <BookReviewButton>
          Book a Workflow Review — {REVIEW.price}
        </BookReviewButton>
      </PremiumSection>
    </PageFrame>
  )
}
